import React, { useState, useRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { Box, Fab, Alert, CircularProgress, Chip, LinearProgress, Grid, Typography, useMediaQuery, useTheme, Button } from '@mui/material';
import { Mic, MicOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';

export interface AudioRecorderProps {
    onRecordingStart?: () => void;
    onRecordingStop?: (audioData: { audioBlobUrl: string; audioBlob: Blob; audioDuration: number }) => void;
    onRecordingError?: (error: string) => void;
    minimalist?: boolean;
    disabled?: boolean;
    maxDuration?: number; // in seconds
    size?: 'small' | 'medium' | 'large';
    showPlayback?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

export interface AudioRecorderRef {
    isRecording: boolean;
    hasBlob: boolean;
    duration: number;
    getMediaBlob: () => Promise<Blob | null>;
    clear: () => void;
}

const AudioRecorder = React.forwardRef<AudioRecorderRef, AudioRecorderProps>(
    (
        {
            onRecordingStart,
            onRecordingStop,
            onRecordingError,
            minimalist = false,
            disabled = false,
            maxDuration = 300, // 5 minutes default
            size = 'medium',
            showPlayback = false,
            className,
            style,
        },
        ref,
    ) => {
        const theme = useTheme();
        const isMobile = useMediaQuery(theme.breakpoints.down('md'));
        const { t } = useTranslation();

        // === STATE ===
        const [isRecording, setIsRecording] = useState(false);
        const [isProcessing, setIsProcessing] = useState(false);
        const [recordingDuration, setRecordingDuration] = useState(0);
        const [error, setError] = useState<string | null>(null);
        const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
        const [audioUrl, setAudioUrl] = useState<string | null>(null);

        // === REFS ===
        const recorderRef = useRef<RecordRTC | null>(null);
        const streamRef = useRef<MediaStream | null>(null);
        const audioUrlRef = useRef<string | null>(null);
        const timerRef = useRef<number | null>(null);
        const startTimeRef = useRef<number>(0);

        // === HANDLERS ===
        const stopRecordingInternal = useCallback(() => {
            if (!recorderRef.current || recorderRef.current.getState() !== 'recording') {
                return;
            }

            setIsProcessing(true);
            setIsRecording(false);

            // Stop animation frame
            if (timerRef.current) {
                cancelAnimationFrame(timerRef.current);
                timerRef.current = null;
            }

            // Calculate final duration
            const finalDuration = startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : 0;
            setRecordingDuration(finalDuration);

            // Stop recording and get blob
            recorderRef.current.stopRecording(() => {
                const blob = recorderRef.current?.getBlob();

                if (blob) {
                    const url = URL.createObjectURL(blob);

                    // Clean up old URL
                    if (audioUrlRef.current) {
                        URL.revokeObjectURL(audioUrlRef.current);
                    }
                    audioUrlRef.current = url;

                    setAudioUrl(url);
                    setAudioBlob(blob);

                    // Callback with audio data
                    onRecordingStop?.({
                        audioBlobUrl: url,
                        audioBlob: blob,
                        audioDuration: finalDuration,
                    });
                }

                // Clean up stream
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((track) => track.stop());
                    streamRef.current = null;
                }

                // Destroy recorder
                if (recorderRef.current) {
                    recorderRef.current.destroy();
                    recorderRef.current = null;
                }

                setIsProcessing(false);
            });
        }, [onRecordingStop]);

        const handleStartRecording = useCallback(async () => {
            try {
                setError(null);
                setIsProcessing(true);
                setRecordingDuration(0);
                setAudioBlob(null);

                // Clean up previous audio URL
                if (audioUrlRef.current) {
                    URL.revokeObjectURL(audioUrlRef.current);
                    audioUrlRef.current = null;
                }
                setAudioUrl(null);

                // Get user media stream
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: false,
                        noiseSuppression: false,
                        autoGainControl: true,
                        sampleRate: 48000,
                        channelCount: 1,
                    },
                });
                streamRef.current = stream;

                // Initialize RecordRTC with optimized settings for immediate start
                const recorder = new RecordRTC(stream, {
                    type: 'audio',
                    mimeType: 'audio/webm',
                    recorderType: StereoAudioRecorder,
                    numberOfAudioChannels: 1,
                    desiredSampRate: 48000,
                    bufferSize: 4096, // Smaller buffer for faster start
                    disableLogs: true,
                });

                recorderRef.current = recorder;

                // Mark as recording BEFORE starting to give immediate feedback
                setIsRecording(true);
                setIsProcessing(false);
                startTimeRef.current = Date.now();

                // Start recording immediately
                recorder.startRecording();

                // Start duration timer
                const updateDuration = () => {
                    if (!recorderRef.current || recorderRef.current.getState() !== 'recording') {
                        return;
                    }

                    const currentDuration = (Date.now() - startTimeRef.current) / 1000;
                    setRecordingDuration(currentDuration);

                    // Auto-stop if max duration reached
                    if (maxDuration && currentDuration >= maxDuration) {
                        stopRecordingInternal();
                        return;
                    }

                    timerRef.current = requestAnimationFrame(updateDuration);
                };

                timerRef.current = requestAnimationFrame(updateDuration);
                onRecordingStart?.();
            } catch (err) {
                console.error('🎤 Failed to start recording:', err);
                const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
                setError(errorMessage);
                onRecordingError?.(errorMessage);
                setIsProcessing(false);
                setIsRecording(false);
            }
        }, [maxDuration, onRecordingStart, onRecordingError, stopRecordingInternal]);

        const handleStopRecording = useCallback(() => {
            stopRecordingInternal();
        }, [stopRecordingInternal]);

        const handleClearState = useCallback(() => {
            setIsRecording(false);
            setIsProcessing(false);
            setRecordingDuration(0);
            setError(null);
            setAudioBlob(null);

            // Clean up blob URL
            if (audioUrlRef.current) {
                URL.revokeObjectURL(audioUrlRef.current);
                audioUrlRef.current = null;
            }
            setAudioUrl(null);

            // Stop animation frame
            if (timerRef.current) {
                cancelAnimationFrame(timerRef.current);
                timerRef.current = null;
            }

            // Stop and destroy recorder
            if (recorderRef.current) {
                if (recorderRef.current.getState() === 'recording') {
                    recorderRef.current.stopRecording(() => {
                        recorderRef.current?.destroy();
                        recorderRef.current = null;
                    });
                } else {
                    recorderRef.current.destroy();
                    recorderRef.current = null;
                }
            }

            // Stop stream tracks
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => {
                    track.stop();
                });
                streamRef.current = null;
            }

            startTimeRef.current = 0;
        }, []);

        // === CLEANUP ===
        useEffect(() => {
            return () => {
                // Stop animation frame
                if (timerRef.current) {
                    cancelAnimationFrame(timerRef.current);
                    timerRef.current = null;
                }

                // Stop and destroy recorder
                if (recorderRef.current) {
                    if (recorderRef.current.getState() === 'recording') {
                        recorderRef.current.stopRecording(() => {
                            recorderRef.current?.destroy();
                        });
                    } else {
                        recorderRef.current.destroy();
                    }
                    recorderRef.current = null;
                }

                // Stop stream tracks
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((track) => {
                        track.stop();
                    });
                    streamRef.current = null;
                }

                // Clean up blob URL
                if (audioUrlRef.current) {
                    URL.revokeObjectURL(audioUrlRef.current);
                    audioUrlRef.current = null;
                }
            };
        }, []);

        // === EXPOSE REF METHODS ===
        useImperativeHandle(
            ref,
            () => ({
                isRecording,
                duration: recordingDuration,
                hasBlob: !!audioBlob,
                getMediaBlob: async () => audioBlob,
                clear: handleClearState,
            }),
            [audioBlob, recordingDuration, isRecording, handleClearState],
        );

        // === RENDER HELPERS ===
        const getFabSize = () => {
            switch (size) {
                case 'small':
                    return { width: 48, height: 48 };
                case 'large':
                    return { width: 96, height: 96 };
                default:
                    return { width: 72, height: 72 };
            }
        };

        const getIconSize = () => {
            switch (size) {
                case 'small':
                    return 20;
                case 'large':
                    return 40;
                default:
                    return 28;
            }
        };

        const formatTime = (seconds: number) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        return (
            <Box className={className} style={{...style, width: '100%'}}>
                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Mobile View - Horizontal Orange Button */}
                {isMobile ? (
                    <Box display="flex" flexDirection="column" alignItems="stretch" gap={2} width="100%">
                        {/* Outer container with light pink border */}
                        <Box
                            sx={{
                                width: '100%',
                                p: 1,
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'rgba(255, 182, 193, 0.5)',
                                backgroundColor: 'white',
                            }}
                        >
                            <Button
                                onClick={isRecording ? handleStopRecording : handleStartRecording}
                                disabled={disabled || isProcessing}
                                fullWidth
                                sx={{
                                    background: theme.palette.primary.main,
                                    color: 'white',
                                    borderRadius: 2,
                                    py: 1.5,
                                    px: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    justifyContent: 'flex-start',
                                }}
                            >
                                {isProcessing ? (
                                    <CircularProgress size={24} color="inherit" sx={{ color: 'white' }} />
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            border: '2px solid white',
                                        }}
                                    >
                                        <Mic
                                            sx={{
                                                fontSize: 20,
                                                color: 'white',
                                            }}
                                        />
                                    </Box>
                                )}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        flex: 1,
                                        textAlign: 'left',
                                    }}
                                >
                                    {isRecording
                                        ? t('components.conversationInput.stopRecording', {
                                              defaultValue: 'Dừng ghi âm',
                                          })
                                        : t('components.conversationInput.startRecording', {
                                              defaultValue: 'Bắt đầu ghi âm',
                                          })}
                                </Typography>
                            </Button>
                        </Box>

                        {/* Recording Status for Mobile */}
                        {!minimalist && isRecording && (
                            <Box textAlign="center" width="100%">
                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                    <Chip
                                        label={formatTime(recordingDuration)}
                                        color="error"
                                        size="small"
                                        variant="outlined"
                                    />
                                    {maxDuration && (
                                        <Typography variant="caption" color="text.secondary">
                                            / {formatTime(maxDuration)}
                                        </Typography>
                                    )}
                                </Box>
                                {maxDuration && (
                                    <LinearProgress
                                        variant="determinate"
                                        value={(recordingDuration / maxDuration) * 100}
                                        sx={{
                                            mt: 1,
                                            width: '100%',
                                            maxWidth: '300px',
                                            height: 4,
                                            borderRadius: 2,
                                        }}
                                        color={recordingDuration > maxDuration * 0.9 ? 'warning' : 'primary'}
                                    />
                                )}
                            </Box>
                        )}

                        {/* Audio Playback for Mobile */}
                        {audioUrl && showPlayback && !isRecording && (
                            <Box width="100%" maxWidth="400px">
                                <audio src={audioUrl} controls style={{ width: '100%' }} />
                            </Box>
                        )}
                    </Box>
                ) : (
                    /* Desktop View - Original Design */
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Grid container>
                        <Grid item>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                <Fab
                                    color={isRecording ? 'error' : 'primary'}
                                    onClick={isRecording ? handleStopRecording : handleStartRecording}
                                    disabled={disabled || isProcessing}
                                    sx={{
                                        ...getFabSize(),
                                        position: 'relative',
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                        },
                                    }}
                                >
                                    {isProcessing ? (
                                        <CircularProgress size={getIconSize()} color="inherit" />
                                    ) : isRecording ? (
                                        <MicOff sx={{ fontSize: getIconSize() }} />
                                    ) : (
                                        <Mic sx={{ fontSize: getIconSize() }} />
                                    )}
                                </Fab>
                            </Box>
                        </Grid>
                        <Grid item display="flex" alignItems="center" justifyContent="center">
                            {/* Recording Status */}
                            {!minimalist && (
                                <Box textAlign="center">
                                    {/* Recording Duration */}
                                    {isRecording && (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Chip
                                                label={formatTime(recordingDuration)}
                                                color="error"
                                                size="small"
                                                variant="outlined"
                                            />
                                            {maxDuration && (
                                                <Typography variant="caption" color="text.secondary">
                                                    / {formatTime(maxDuration)}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {/* Duration Progress Bar */}
                                    {isRecording && maxDuration && (
                                        <LinearProgress
                                            variant="determinate"
                                            value={(recordingDuration / maxDuration) * 100}
                                            sx={{
                                                mt: 1,
                                                width: 120,
                                                height: 4,
                                                borderRadius: 2,
                                                transition: 'all 0.3s ease-in-out',
                                            }}
                                            color={recordingDuration > maxDuration * 0.9 ? 'warning' : 'primary'}
                                        />
                                    )}
                                </Box>
                            )}

                            {/* Audio Playback */}
                            {audioUrl && showPlayback && !isRecording && (
                                <audio src={audioUrl} controls style={{ marginLeft: 8 }} />
                            )}
                        </Grid>
                    </Grid>
                </Box>
                )}
            </Box>
        );
    },
);

AudioRecorder.displayName = 'AudioRecorder';

export default AudioRecorder;
